import { useEffect, useRef, useState } from 'react';

interface UseScrollAnimationOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
}

// Detect environments where the scroll reveal should be skipped entirely:
// - SSR or pre-hydration (no window).
// - User has prefers-reduced-motion enabled.
// - Browser without IntersectionObserver support.
// In any of these cases we default isVisible=true so the page renders the
// final state immediately — the opacity-0 starting state never sticks.
function shouldStartVisible(): boolean {
  if (typeof window === 'undefined') return true;
  if (typeof IntersectionObserver === 'undefined') return true;
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true;
  return false;
}

export function useScrollAnimation<T extends HTMLElement = HTMLDivElement>(
  options: UseScrollAnimationOptions = {},
) {
  // Use a rootMargin tuned for sections that may load already in the viewport.
  // The old default ('0px 0px -50px 0px') could fail to trigger when a section
  // straddles the fold on initial paint — the entry never crossed the negative
  // margin and isVisible stayed false, leaving sections invisible.
  // '0px 0px -10% 0px' gives the observer enough slack to fire on first paint
  // for above-the-fold sections.
  const { threshold = 0.1, rootMargin = '0px 0px -10% 0px', triggerOnce = true } = options;
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState<boolean>(() => shouldStartVisible());

  useEffect(() => {
    // Initial state from useState() already accounts for shouldStartVisible(),
    // so when it would return true the observer doesn't need to be attached.
    // Skipping the work also avoids a redundant setState-in-effect (flagged by
    // react-hooks/set-state-in-effect).
    if (shouldStartVisible()) return;
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            if (triggerOnce) {
              observer.unobserve(entry.target);
            }
          } else if (!triggerOnce) {
            setIsVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    // Safety net — if for any reason the observer never fires within 600ms
    // (e.g., layout shift, observer attached to a not-yet-laid-out element),
    // promote to visible so the section never stays at opacity-0.
    const fallback = window.setTimeout(() => {
      setIsVisible((current) => current || true);
    }, 600);

    return () => {
      window.clearTimeout(fallback);
      observer.disconnect();
    };
  }, [threshold, rootMargin, triggerOnce]);

  return { ref, isVisible };
}

export function useStaggerAnimation(itemCount: number, baseDelay: number = 100) {
  const [visibleItems, setVisibleItems] = useState<boolean[]>(new Array(itemCount).fill(false));
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Stagger the reveal of each item
            for (let i = 0; i < itemCount; i++) {
              setTimeout(() => {
                setVisibleItems((prev) => {
                  const newState = [...prev];
                  newState[i] = true;
                  return newState;
                });
              }, i * baseDelay);
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [itemCount, baseDelay]);

  return { containerRef, visibleItems };
}
